import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, isToday } from 'date-fns'
import { ChevronDownIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { parseCalendarDay } from '@/utils/format'

/** The API stores dates as an offset-qualified ISO timestamp. */
const ISO_WITH_OFFSET = "yyyy-MM-dd'T'HH:mm:ssxxx"
/** A calendar day, with no zone and no time — how the API spells a budget day. */
const DAY = 'yyyy-MM-dd'

export interface DateTimePickerProps {
  /** ISO timestamp — or a `yyyy-MM-dd` day when `dateOnly` — and '' when unset. */
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Days before this one cannot be picked. */
  fromDate?: Date
  /**
   * Time to fill in when a date is picked and no time has been chosen yet.
   * 'start' is 00:00 — or the current time when the picked day is today.
   * 'end' is 23:59.
   */
  defaultTime?: 'start' | 'end'
  /**
   * Drops the time of day: no time field, and the value is a plain calendar
   * day (`yyyy-MM-dd`). What a budget period is spelled in.
   */
  dateOnly?: boolean
  /** Whether the field may be emptied again. Off where a date is mandatory. */
  clearable?: boolean
}

export function DateTimePicker({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  fromDate,
  defaultTime = 'start',
  dateOnly,
  clearable = true,
}: DateTimePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const timeId = useId()

  // A day has no zone of its own, so it is parsed as a local one — otherwise
  // the browser reads `yyyy-MM-dd` as UTC and shows the day before.
  const parsed = value ? (dateOnly ? parseCalendarDay(value) : new Date(value)) : undefined
  const selected = parsed && !isNaN(parsed.getTime()) ? parsed : undefined

  const emit = (d: Date) => onChange(format(d, dateOnly ? DAY : ISO_WITH_OFFSET))

  /** Fills in the time for a day that has none yet. */
  const applyDefaultTime = (d: Date) => {
    if (defaultTime === 'end') {
      d.setHours(23, 59, 0, 0)
    } else if (isToday(d)) {
      const now = new Date()
      d.setHours(now.getHours(), now.getMinutes(), 0, 0)
    } else {
      d.setHours(0, 0, 0, 0)
    }
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return
    const next = new Date(date)
    // Keep the time that was already picked, otherwise fall back to the default.
    if (selected) {
      next.setHours(selected.getHours(), selected.getMinutes(), 0, 0)
    } else {
      applyDefaultTime(next)
    }
    emit(next)
    // With a time to set as well, the popover stays open for it.
    if (dateOnly) setOpen(false)
  }

  const handleTimeChange = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    if (isNaN(hours) || isNaN(minutes)) return
    const next = new Date(selected ?? new Date())
    next.setHours(hours, minutes, 0, 0)
    emit(next)
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="field"
            disabled={disabled}
            className="flex-1 justify-between"
          >
            {selected ? (
              format(selected, dateOnly ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm')
            ) : (
              <span className="text-muted-foreground">
                {placeholder ?? t('common.selectDateTime')}
              </span>
            )}
            <ChevronDownIcon className="size-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            captionLayout="dropdown"
            onSelect={handleDateSelect}
            disabled={fromDate ? { before: fromDate } : undefined}
          />
          {/* The time belongs to the picked day, so it is set in the same place —
              as is emptying the field, rather than a button beside it. */}
          {(!dateOnly || clearable) && (
            <div className="flex items-center gap-2 border-t p-3">
              {!dateOnly && (
                <>
                  <label htmlFor={timeId} className="text-sm text-muted-foreground">
                    {t('common.time')}
                  </label>
                  <Input
                    id={timeId}
                    type="time"
                    step={60}
                    value={selected ? format(selected, 'HH:mm') : ''}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="w-28 appearance-none bg-transparent dark:bg-transparent [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                </>
              )}
              <div className="ml-auto flex items-center gap-1">
                {clearable && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!selected}
                    className="text-muted-foreground"
                    onClick={() => {
                      onChange('')
                      setOpen(false)
                    }}
                  >
                    {t('common.clearDate')}
                  </Button>
                )}
                {!dateOnly && (
                  <Button type="button" size="sm" onClick={() => setOpen(false)}>
                    {t('common.done')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
